<script setup lang="ts">
import { inject, ref, type PropType, type Ref } from "vue";
import SideNavButton from "~/types/sideNavButton";
import { useGlobalComponentStore } from "~/stores/globalComponentStore";
const globalComponentStore = useGlobalComponentStore();
const closeShowMore = () => {
  if (globalComponentStore._showMoreModal) {
    globalComponentStore.setShowMoreModalVisibility(false);
  }
};
const props = defineProps({
  sideNavData: { required: true, type: Object as PropType<SideNavButton> },
});

// Supplied by SideNav while the rail is hover-expanded. The default keeps this
// component usable outside the rail (and inside the mobile "More" sheet, whose
// desktop branch never renders anyway).
//
// A flat item needs no hover tooltip any more: pointing at the row is what
// expands the rail, so the name is always spelled out beside the icon a beat
// later. Showing a tooltip first only made it flash and vanish.
const isRailExpanded = inject<Ref<boolean>>("sideNavExpanded", ref(false));
</script>
<template>
  <!-- Desktop rail. The row spans the full panel width in both states so the
       icon can sit centred while collapsed and stretch into a labelled row
       once the panel opens. -->
  <div
    class="nav-row hidden sm:flex"
    :class="{ 'is-expanded': isRailExpanded }"
  >
    <NuxtLink
      :to="`/${props.sideNavData.path}`"
      class="side-icon nav-entry"
      @click="closeShowMore"
    >
      <img
        :src="`/svgs/sidebar/${props.sideNavData.imgName}.svg`"
        class="side-icon-img shrink-0"
      />
      <span class="nav-label">{{ props.sideNavData.name }}</span>
    </NuxtLink>
  </div>
  <!-- Mobile "More" sheet: full label inline, unchanged. -->
  <NuxtLink
    :to="`/${props.sideNavData.path}`"
    class="side-icon sm:hidden"
    @click="closeShowMore"
  >
    <div class="flex items-center gap-2 text-white sm:hidden">
      <img
        :src="`/svgs/sidebar/${props.sideNavData.imgName}.svg`"
        class="side-icon-img"
      />
      <div>
        <div>
          {{ props.sideNavData.name }}
        </div>
      </div>
    </div>
  </NuxtLink>
</template>
<style scoped>
.router-link-active {
  background-color: rgb(255 255 255 / 0.2);
  border-color: rgb(255 255 255 / 0.5);
  box-shadow: 0 12px 22px rgba(0, 0, 0, 0.25);
}

@media (min-width: 640px) {
  .nav-row {
    justify-content: center;
  }

  .nav-row.is-expanded {
    justify-content: flex-start;
  }

  /* Collapsed: a centred icon square, as before. Expanded: the button grows to
     the full row and the label slides out beside the icon. Only width/opacity
     animate — a transform here would turn the panel into a containing block for
     the fixed flyouts its siblings rely on. */
  .nav-entry {
    flex: 0 0 auto;
    align-items: center;
    overflow: hidden;
    transition:
      flex-grow var(--rail-motion, 200ms) var(--rail-ease, ease),
      width var(--rail-motion, 200ms) var(--rail-ease, ease),
      background-color 150ms ease;
  }

  .nav-row.is-expanded .nav-entry {
    flex: 1 1 auto;
    gap: 0.625rem;
  }

  .nav-label {
    max-width: 0;
    opacity: 0;
    white-space: nowrap;
    overflow: hidden;
    color: #fff;
    font-size: 0.875rem;
    line-height: 1.25rem;
    transition:
      max-width var(--rail-motion, 200ms) var(--rail-ease, ease),
      opacity var(--rail-motion, 200ms) var(--rail-ease, ease);
  }

  .nav-row.is-expanded .nav-label {
    max-width: 11rem;
    opacity: 1;
  }
}
</style>
